import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import classNames from "classnames";
import { useState } from "react";

import "dayjs/locale/da";
import "dayjs/locale/de";
import "dayjs/locale/fi";
import "dayjs/locale/fr";
import "dayjs/locale/nn";
import "dayjs/locale/sv";

import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import StepContent from "@mui/material/StepContent";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import { SelectChangeEvent } from "@mui/material";

import styles from "./complaint-forms.module.scss";
import TextField from "@/components/atoms/text-field/text-field";
import FormControlLabel from "@/components/atoms/form-label/label";
import Button from "@/components/atoms/button/button";
import { Select, Option } from "@/components/atoms/select-dropdown/select";
import StepBox from "@/components/atoms/step-box/step-box";

import TransactionsStep from "@/components/molecules/transactions/transactions";
import CustomerDetailsStep from "@/components/molecules/customer-details/customer-details";
import SummaryStep from "@/components/molecules/summary/summary";
import { usePrefilledStatus, getPrefilledData } from "@/helpers/prefill-hook";
import { getSupportEmail } from "@/helpers/support-email";
import setCsrfCookie from "@/helpers/csrfHelper";
import { goToPage } from "@/helpers/stepHelper";

import DisputePage from "@/components/organisms/dispute-page/page";

import { GetServerSidePropsContext } from "next";
import InputFile from "@/components/atoms/input-file/input-file";

import { getBlockCardNumber } from "@/brands";

import PageSetup from "@/helpers/PageSetup";
import { client, server } from "@/helpers/logger";

let lastLog = "";

function PageStolen(props: Readonly<{ locale: string; csrfToken: string }>) {
  const {
    t,
    brand,
    data,
    country,
    mailto,
    isDevelopment,
    activeStep,
    setActiveStep,
    succeededStep,
    setSucceededStep,
    isSessionExpired,
  } = PageSetup(props);

  const title: string = `${t("Cardholder dispute")} | ${t(
    "Stolen or lost card",
  )}`;

  const [isCardBlocked, setIsCardBlocked] = useState("");
  usePrefilledStatus(
    "what-happened[have-you-blocked-your-card]",
    data,
    (val) => {
      if (typeof val === "string" && !isCardBlocked.length && val.length) {
        setIsCardBlocked(val);
      }
    },
  );

  const supportEmail = getSupportEmail(
    `${brand}`,
    country ? `${country}` : undefined,
    props.locale,
    "stolen",
  );

  const emailTo = mailto || supportEmail;

  if (mailto && supportEmail !== lastLog) {
    client.log("SupportEmail", supportEmail);
    client.log("TestingEmail", mailto);
    lastLog = supportEmail;
  }

  const isNotNorwayMarket =
    (country && country !== "no") || (!country && props.locale != "no");
  const isNotSwedenMarket =
    (country && country !== "sv") || (!country && props.locale != "sv");

  const whatHappened = (
    <>
      <input type="hidden" name="mailto" value={emailTo} />
      <input
        type="hidden"
        name="email-subject"
        value="I have lost my card/my card is stolen"
      />
      <input
        type="hidden"
        name="what-happened[what-happened]"
        value="I have lost my card/my card is stolen"
      />
      <input
        type="hidden"
        name="what-happened[what-happened]"
        value="I neither made nor authorized the transaction(s)"
      />
      {isCardBlocked === "No" ? (
        <Alert
          severity="error"
          role="alert"
          tabIndex={-1}
          id="blocked-card-desc"
        >
          <AlertTitle>
            {t(
              "You need to block your card immediately and file a police report",
            )}
          </AlertTitle>
          <Typography>
            {t("Your card can be blocked in your card app or by calling us at")}{" "}
            <a
              href={`tel:${getBlockCardNumber(country, props.locale)?.replace(
                /\s/g,
                "",
              )}`}
            >
              {getBlockCardNumber(country, props.locale)}
            </a>
            .
          </Typography>
        </Alert>
      ) : null}
      <FormControlLabel
        labelPlacement="top"
        control={
          <Select
            aria-describedby="blocked-card-desc"
            value={isCardBlocked}
            name="what-happened[have-you-blocked-your-card]"
            inputProps={{
              required: activeStep === 0,
              pattern: "Yes",
            }}
            onChange={(e: SelectChangeEvent<unknown>) => {
              const selectedIndex = (e.target as HTMLSelectElement)
                .selectedIndex;
              if (selectedIndex === 0) {
                setIsCardBlocked("");
              }
              if (selectedIndex === 1) {
                setIsCardBlocked("Yes");
              }
              if (selectedIndex === 2) {
                setIsCardBlocked("No");
              }
            }}
          >
            <Option value="" hidden disabled>
              {t("Choose")}
            </Option>
            <Option value={"Yes"}>{t("Yes")}</Option>
            <Option value={"No"}>{t("No")}</Option>
          </Select>
        }
        label={
          <Typography variant="body1" component="span">
            {t("Have you blocked your card")}
          </Typography>
        }
      />
      {isCardBlocked === "Yes" ? (
        <>
          <InputFile
            label={
              <Typography variant="body1" component="span">
                {t("Submit a copy of your police report")}
              </Typography>
            }
            required={
              activeStep === 0 && isNotNorwayMarket && isNotSwedenMarket
            }
            multiple={true}
            name="what-happened[attached-files]"
            title={
              t("We can not handle your dispute without a police report") ||
              "We can not handle your dispute without a police report"
            }
          />
          <FormControlLabel
            labelPlacement="top"
            control={
              <TextField
                rows={8}
                multiline
                required={activeStep === 0}
                autoComplete="off"
                name="what-happened[describe-what-happened]"
                defaultValue={
                  isDevelopment
                    ? getPrefilledData(
                        "what-happened[describe-what-happened]",
                        data,
                      )
                    : "" /* no personal data in prod */
                }
              />
            }
            label={
              <Typography variant="body1" component="span">
                {t("Describe as detailed as possible of what happened")}
              </Typography>
            }
          />
        </>
      ) : null}
    </>
  );

  return (
    /* @ts-ignore */
    <DisputePage
      title={title}
      csrfToken={props.csrfToken}
      locale={props.locale}
      activeStep={activeStep}
      setActiveStep={setActiveStep}
      succeededStep={succeededStep}
      setSucceededStep={setSucceededStep}
      form="stolen"
      isSessionExpired={isSessionExpired}
      header={
        <>
          <Typography
            variant="h5"
            component="h1"
            className={classNames([styles.h1])}
            tabIndex={-1}
          >
            {t("Stolen or lost card")}
          </Typography>
          <Typography variant="body1" className={classNames([styles.h2])}>
            {t(
              "To proceed with your dispute, you have to fill out the information below",
            )}
          </Typography>
        </>
      }
      childrenStepsDesktop={
        <Stepper activeStep={activeStep} orientation="vertical">
          <Step key={t("What happened")} completed={activeStep > 0}>
            <StepLabel>
              <Typography variant="h6" component="h2">
                {t("What happened")}
              </Typography>
            </StepLabel>
          </Step>
          <Step key={t("Transaction details")} completed={activeStep > 1}>
            <StepLabel>
              <Typography variant="h6" component="h2">
                {t("Transaction details")}
              </Typography>
            </StepLabel>
          </Step>
          <Step key={t("Customer information")} completed={activeStep > 2}>
            <StepLabel>
              <Typography variant="h6" component="h2">
                {t("Customer information")}
              </Typography>
            </StepLabel>
          </Step>
          <Step key={t("Summary")} completed={activeStep > 3}>
            <StepLabel>
              <Typography variant="h6" component="h2">
                {t("Summary")}
              </Typography>
            </StepLabel>
          </Step>
        </Stepper>
      }
      childrenDesktop={
        <>
          <div style={{ display: activeStep !== 0 ? "none" : "" }}>
            <Stack
              sx={{
                gap: 2,
              }}
            >
              {whatHappened}
              <StepBox>
                <Button
                  type={"submit"}
                  variant="contained"
                  data-adobe-eventname="cta"
                >
                  {t("Next")}
                </Button>
              </StepBox>
            </Stack>
          </div>
          <div style={{ display: activeStep !== 1 ? "none" : "" }}>
            <Stack
              sx={{
                gap: 2,
              }}
            >
              {succeededStep > 0 ? (
                <TransactionsStep
                  data={data}
                  activeStep={activeStep}
                  setActiveStep={setActiveStep}
                  setSucceededStep={setSucceededStep}
                />
              ) : null}
              <StepBox>
                <Button
                  type="button"
                  onClick={() => {
                    goToPage(activeStep - 1, setActiveStep);
                  }}
                  variant="contained"
                >
                  {t("Back")}
                </Button>

                <Button
                  type="submit"
                  variant="contained"
                  data-adobe-eventname="cta"
                >
                  {t("Next")}
                </Button>
              </StepBox>
            </Stack>
          </div>
          <div style={{ display: activeStep !== 2 ? "none" : "" }}>
            <Stack
              sx={{
                gap: 2,
              }}
            >
              {succeededStep > 1 ? (
                <CustomerDetailsStep
                  data={data}
                  activeStep={activeStep}
                  setActiveStep={setActiveStep}
                  succeededStep={succeededStep}
                  setSucceededStep={setSucceededStep}
                  brand={brand}
                  locale={props.locale}
                  page={"stolen"}
                  country={country}
                />
              ) : null}
              <StepBox>
                <Button
                  type="button"
                  onClick={() => {
                    goToPage(activeStep - 1, setActiveStep);
                  }}
                  variant="contained"
                  data-adobe-eventname="cta"
                >
                  {t("Back")}
                </Button>

                <Button
                  type="submit"
                  variant="contained"
                  data-adobe-eventname="cta"
                >
                  {t("Next")}
                </Button>
              </StepBox>
            </Stack>
          </div>
          <div style={{ display: activeStep !== 3 ? "none" : "" }}>
            <Stack
              sx={{
                gap: 2,
              }}
            >
              {activeStep > 2 ? (
                <SummaryStep
                  setActiveStep={setActiveStep}
                  activeStep={activeStep}
                  data={data}
                  brand={brand}
                />
              ) : null}
              <StepBox>
                <Button
                  type="button"
                  onClick={() => {
                    goToPage(activeStep - 1, setActiveStep);
                  }}
                  variant="contained"
                >
                  {t("Back")}
                </Button>
                <Button type="submit" variant="contained">
                  {t("Send")}
                </Button>
              </StepBox>
            </Stack>
          </div>
        </>
      }
    >
      <Stepper activeStep={activeStep} orientation={"vertical"}>
        <Step key={t("What happened")} completed={activeStep > 0}>
          <StepLabel>
            <Typography variant="h6" component="h2">
              {t("What happened")}
            </Typography>
          </StepLabel>
          <StepContent TransitionProps={{ unmountOnExit: false }}>
            <Stack
              sx={{
                gap: 2,
              }}
            >
              {whatHappened}

              <div>
                <Button
                  type={"submit"}
                  variant="contained"
                  data-adobe-eventname="cta"
                >
                  {t("Next")}
                </Button>
              </div>
            </Stack>
          </StepContent>
        </Step>
        <Step key={t("Transaction details")} completed={activeStep > 1}>
          <StepLabel>
            <Typography variant="h6" component="h2">
              {t("Transaction details")}
            </Typography>
          </StepLabel>
          <StepContent TransitionProps={{ unmountOnExit: false }}>
            <Stack
              sx={{
                gap: 2,
              }}
            >
              {succeededStep > 0 ? (
                <TransactionsStep
                  data={data}
                  activeStep={activeStep}
                  setActiveStep={setActiveStep}
                  setSucceededStep={setSucceededStep}
                />
              ) : null}
              <div>
                <Button
                  type={"submit"}
                  variant="contained"
                  data-adobe-eventname="cta"
                >
                  {t("Next")}
                </Button>
              </div>
              <div>
                <Button
                  type="button"
                  onClick={() => {
                    goToPage(activeStep - 1, setActiveStep);
                  }}
                  data-adobe-eventname="cta"
                >
                  {t("Back")}
                </Button>
              </div>
            </Stack>
          </StepContent>
        </Step>
        <Step key={t("Customer information")} completed={activeStep > 2}>
          <StepLabel>
            <Typography variant="h6" component="h2">
              {t("Customer information")}
            </Typography>
          </StepLabel>
          <StepContent TransitionProps={{ unmountOnExit: false }}>
            <Stack
              sx={{
                gap: 2,
              }}
            >
              {succeededStep > 1 ? (
                <CustomerDetailsStep
                  activeStep={activeStep}
                  setActiveStep={setActiveStep}
                  succeededStep={succeededStep}
                  setSucceededStep={setSucceededStep}
                  data={data}
                  brand={brand}
                  locale={props.locale}
                  page={"stolen"}
                  country={country}
                />
              ) : null}
              <div>
                <Button
                  type="submit"
                  variant="contained"
                  data-adobe-eventname="cta"
                >
                  {t("Next")}
                </Button>
              </div>
              <div>
                <Button
                  type="button"
                  onClick={() => {
                    goToPage(activeStep - 1, setActiveStep);
                  }}
                  data-adobe-eventname="cta"
                >
                  {t("Back")}
                </Button>
              </div>
            </Stack>
          </StepContent>
        </Step>
        <Step key={t("Summary")} completed={activeStep > 3}>
          <StepLabel>
            <Typography variant="h6" component="h2">
              {t("Summary")}
            </Typography>
          </StepLabel>
          <StepContent TransitionProps={{ unmountOnExit: false }}>
            <Stack
              sx={{
                gap: 2,
              }}
            >
              {activeStep > 2 ? (
                <SummaryStep
                  setActiveStep={setActiveStep}
                  activeStep={activeStep}
                  data={data}
                  brand={brand}
                />
              ) : null}
              <div>
                <Button type="submit" variant="contained">
                  {t("Send")}
                </Button>
              </div>
              <div>
                <Button
                  type="button"
                  onClick={() => {
                    goToPage(activeStep - 1, setActiveStep);
                  }}
                >
                  {t("Back")}
                </Button>
              </div>
            </Stack>
          </StepContent>
        </Step>
      </Stepper>
    </DisputePage>
  );
}

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const { locale, req } = context;
  const csrfToken = setCsrfCookie(context);

  server.logPageVisit(req);

  return {
    props: {
      ...(await serverSideTranslations(locale ?? "en", ["common", "footer"])),
      locale,
      csrfToken,
    },
  };
}

export default PageStolen;
